import { Flex, font, themedColor, useViewportSize } from '@lemonade-hq/cdk';
import { Button, CloseIcon, MEDIA_QUERIES, Option, OptionsList, Select } from '@lemonade-hq/nuis';
import {
  ANALYTICS_SERVICE_URL,
  trackWithAnalyticsService as baseTrackWithAnalyticsService,
  getDefaultAnalyticsProps,
} from '@lemonade-hq/nuiza';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useRegionRedirect } from '../../hooks/useRegionRedirect';

const Dialog = styled(Flex)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding: 0;
  z-index: 99999;

  ${MEDIA_QUERIES.tabletAndDesktop`
    align-items: center;
    padding: 20px;
  `}
`;

const BaseDialog = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${themedColor('white')};
  align-items: center;
  padding: 32px 24px;
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-width: 448px;
  box-shadow: 0px 4px 25px rgba(0, 0, 0, 0.1);
  ${MEDIA_QUERIES.tabletAndDesktop`
    border-radius: 16px;
    padding: 40px;
  `}
`;

const DialogClose = styled.button`
  position: absolute;
  right: 8px;
  top: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: none;
  height: 40px;
  width: 40px;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: block;
  `}
`;

const DialogHeader = styled(Flex)`
  text-align: center;

  h5 {
    ${font('main', { fontSize: '1.8rem', lineHeight: '2.4rem', fontWeight: 700 })};
    text-wrap: balance;
  }
`;

const DialogBody = styled(Flex)`
  width: 100%;
  flex-direction: row;
  gap: 12px;
  margin-top: 24px;
  margin-bottom: 20px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    flex-direction: column;
    gap: 24px;
    margin-top: 32px;
    margin-bottom: 20px;
  `}
`;

const DialogFooter = styled(Flex)`
  width: 100%;
  justify-content: center;
`;

const StyledButton = styled(Button)`
  font-size: 1.6rem;
  width: auto;
  border-radius: 8px;
  width: 100%;
`;

const OptionContainer = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LANGUAGE_OPTIONS = [
  { value: 'us', label: 'United States', icon: '🇺🇸' },
  { value: 'nl', label: 'Netherlands (Nederlands)', icon: '🇳🇱' },
  { value: 'nl/en', label: 'Netherlands (English)', icon: '🇳🇱' },
  { value: 'de', label: 'Germany (Deutsch)', icon: '🇩🇪' },
  { value: 'de/en', label: 'Germany (English)', icon: '🇩🇪' },
  { value: 'fr', label: 'France (Français)', icon: '🇫🇷' },
  { value: 'fr/en', label: 'France (English)', icon: '🇫🇷' },
  { value: 'uk', label: 'United Kingdom', icon: '🇬🇧' },
];

const transformSelectedLanguageToLocale = (language: string) => {
  switch (language) {
    case 'us':
      return 'en-US';
    case 'nl':
      return 'nl-NL';
    case 'de':
      return 'de-DE';
    case 'fr':
      return 'fr-FR';
    case 'uk':
      return 'en-GB';
    case 'nl/en':
      return 'en-NL';
    case 'de/en':
      return 'en-DE';
    case 'fr/en':
      return 'en-FR';
    default:
      return 'en-US';
  }
};

const trackWithAnalyticsService = ({
  eventName,
  customProps,
}: {
  eventName: string;
  customProps: Record<string, string>;
}): void => {
  baseTrackWithAnalyticsService({
    eventName,
    props: { ...getDefaultAnalyticsProps(), ...customProps },
    reportingUrl: ANALYTICS_SERVICE_URL[process.env.NEXT_PUBLIC_STAGE as keyof typeof ANALYTICS_SERVICE_URL],
  });
};

export const SelectRegionDialog: React.FC = () => {
  const { showRegionRedirectBanner, onRedirect, defaultSelectedRegion, onSetBannerInteractedCookie } =
    useRegionRedirect();

  const { isMobileViewport } = useViewportSize();

  const [selectedLanguage, setSelectedLanguage] = useState(defaultSelectedRegion);

  const [closed, setClosed] = useState(false);

  const analyticsProps = useMemo(() => {
    return {
      suggested_locale: transformSelectedLanguageToLocale(defaultSelectedRegion),
    };
  }, [selectedLanguage, defaultSelectedRegion]);

  const handleDismiss = () => {
    setClosed(true);
    trackWithAnalyticsService({ eventName: 'locale_picker.dismissed', customProps: analyticsProps });
    onSetBannerInteractedCookie();
  };

  const handleBackdropDismiss = () => {
    if (!isMobileViewport) return;
    handleDismiss();
  };

  const handleRedirect = useCallback(() => {
    trackWithAnalyticsService({
      eventName: 'locale_picker.submitted',
      customProps: { ...analyticsProps, submitted_locale: transformSelectedLanguageToLocale(selectedLanguage) },
    });
    onRedirect(selectedLanguage);
  }, [selectedLanguage, analyticsProps, onRedirect]);

  useEffect(() => {
    if (!showRegionRedirectBanner || closed) return;
    trackWithAnalyticsService({ eventName: 'locale_picker.viewed', customProps: analyticsProps });
  }, [showRegionRedirectBanner, closed]);

  if (!showRegionRedirectBanner || closed) return null;

  return (
    <Dialog onClick={handleBackdropDismiss}>
      <BaseDialog onClick={e => e.stopPropagation()}>
        <DialogClose onClick={handleDismiss}>
          <CloseIcon colorOverride="#7b7b7b" width={14} height={14} />
        </DialogClose>
        <DialogHeader flexDirection="column" justifyContent="center">
          <h5>Choose your country or region to see the correct coverage options</h5>
        </DialogHeader>
        <DialogBody>
          <Select
            panelPosition={isMobileViewport ? 'top' : 'bottom'}
            value={selectedLanguage}
            onChange={setSelectedLanguage}
          >
            <OptionsList>
              {LANGUAGE_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  <OptionContainer>
                    <span>{option.icon}</span>
                    <span>{option.label}</span>
                  </OptionContainer>
                </Option>
              ))}
            </OptionsList>
          </Select>
        </DialogBody>
        <DialogFooter>
          <StyledButton aria-label="Continue" flavor="bright" onClick={handleRedirect}>
            Continue
          </StyledButton>
        </DialogFooter>
      </BaseDialog>
    </Dialog>
  );
};
